/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {Link as RouteLink} from 'react-router-dom'
import {useIntl} from 'react-intl'

// Components
import {
    Box,
    Icon,
    PopoverTrigger,

    // Hooks
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import Link from '@salesforce/retail-react-app/app/components/link'
import {ChevronDownIcon} from '../../icons'

/* Hooks */
import useEffectiveHeaderVariant from '../../header/partial/useEffectiveHeaderVariant'

// Others
import {categoryUrlBuilder} from '../../../utils/url'

const POPOVER_ARIA_PROPS = ['id', 'aria-haspopup', 'aria-expanded', 'aria-controls']

// Popper anchor for a CMS block that may not forward refs. PopoverTrigger injects
// popover ARIA props, which a role-less span may not carry (axe aria-allowed-attr).
const CustomTriggerWrapper = React.forwardRef(({children, ...props}, ref) => {
    POPOVER_ARIA_PROPS.forEach((key) => delete props[key])
    return (
        <Box ref={ref} as="span" display="inline-flex" {...props}>
            {children}
        </Box>
    )
})
CustomTriggerWrapper.displayName = 'CustomTriggerWrapper'
CustomTriggerWrapper.propTypes = {
    children: PropTypes.node
}

const ListMenuTrigger = ({
    item,
    name,
    isOpen,
    isActive,
    onKeyboardToggle,
    toggleButtonRef,
    popoverId,
    /* Optional overrides for CMS CategoryAddReplace CTA used as a popover trigger.
     * When provided, these win over the category-derived defaults so the trigger
     * mirrors the CTA's authored "Mega Menu Link" appearance. */
    displayName,
    ariaLabel,
    extraLinkProps,
    extraHoverProps,
    triggerSx,
    /* When provided, replaces the default <Link> inside <PopoverTrigger>.
     * Used for non-MegaMenuLink CMS CTAs (Button Solid, etc.) so the popover's
     * trigger keeps the block's authored button styling. */
    customTrigger
}) => {
    const baseStyle = useMultiStyleConfig('ListMenu', {})
    const cmsCategoryReplacement = item.CategoryReplacement?.ContentLink?.Expanded
    const {isTransparent: isTransparentHeader} = useEffectiveHeaderVariant()
    const {formatMessage} = useIntl()
    const labelText = displayName || cmsCategoryReplacement?.OverrideName || name
    const hasHoverOverrides = extraHoverProps && Object.keys(extraHoverProps).length > 0

    return (
        <Box {...baseStyle.listMenuTriggerContainer}>
            <PopoverTrigger data-testid="list-menu-popover-trigger">
                {customTrigger ? (
                    <CustomTriggerWrapper>{customTrigger}</CustomTriggerWrapper>
                ) : (
                    <Link
                        as={RouteLink}
                        to={categoryUrlBuilder(item)}
                        {...baseStyle.listMenuTriggerLink}
                        {...{name: name + ' __'}}
                        {...(isOpen || isActive
                            ? {
                                  ...baseStyle.listMenuTriggerLinkActive
                              }
                            : {})}
                        /* CMS Props - suppressed on transparent header to prevent contrast issues */
                        color={isTransparentHeader ? undefined : cmsCategoryReplacement?.TextColor}
                        backgroundColor={
                            isTransparentHeader
                                ? undefined
                                : cmsCategoryReplacement?.TextBackgroundColor
                        }
                        {...(triggerSx ? {position: 'relative', sx: triggerSx} : {})}
                        /* CMS CTA-authored overrides win over the category defaults above */
                        {...(extraLinkProps || {})}
                        {...(ariaLabel ? {'aria-label': ariaLabel} : {})}
                        {...(hasHoverOverrides
                            ? {_hover: {textDecoration: 'none', ...extraHoverProps}}
                            : {})}
                    >
                        {labelText}
                    </Link>
                )}
            </PopoverTrigger>

            {/* Keyboard-only toggle button — out of flow, so it neither shifts
                layout on focus nor pads the nav item. It used to sit in flow
                with a fixed 8px footprint, which widened every item and left
                the visible menu text off-centre by half that trailing width.
                Icon and focus ring are invisible until keyboard-focused. */}
            <Box
                as="button"
                type="button"
                ref={toggleButtonRef}
                aria-expanded={isOpen}
                aria-controls={popoverId}
                aria-label={formatMessage(
                    {
                        id: 'list_menu.submenu_toggle.label',
                        defaultMessage: '{name} submenu'
                    },
                    {name}
                )}
                onClick={onKeyboardToggle}
                sx={{
                    /* Out of flow: keeps its place beside the label without
                     * contributing width, so the nav centres on what is visible */
                    position: 'absolute',
                    left: '100%',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    width: '8px',
                    height: '16px',
                    padding: 0,
                    border: 0,
                    borderRadius: '2px',
                    background: 'none',
                    cursor: 'pointer',
                    /* Icon invisible by default */
                    opacity: 0,
                    outline: 'none',
                    /* Reveal icon and focus ring only on keyboard focus */
                    '&:focus-visible, &[data-focus-visible-added]': {
                        opacity: 1,
                        outline: '2px solid',
                        outlineColor: 'webTheme.focus',
                        outlineOffset: '2px'
                    }
                }}
            >
                <Icon
                    as={ChevronDownIcon}
                    width="12px"
                    height="12px"
                    transition="transform 0.2s ease"
                    transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                    aria-hidden="true"
                />
            </Box>
        </Box>
    )
}

ListMenuTrigger.propTypes = {
    item: PropTypes.object,
    name: PropTypes.string,
    isOpen: PropTypes.bool,
    isActive: PropTypes.bool,
    onKeyboardToggle: PropTypes.func,
    toggleButtonRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    popoverId: PropTypes.string,
    displayName: PropTypes.node,
    ariaLabel: PropTypes.string,
    extraLinkProps: PropTypes.object,
    extraHoverProps: PropTypes.object,
    triggerSx: PropTypes.object,
    customTrigger: PropTypes.node
}

export {ListMenuTrigger}
